/** @typedef {'AGAINST_PO' | 'LOAN_RENTAL' | 'FROM_AIRCRAFT'} ReceivingContext */

function hasFromAircraftLines(record) {
  return (Array.isArray(record.items) ? record.items : []).some((line) => {
    const scope = String(line?.aircraftScope || '').trim().toUpperCase();
    const reg = String(line?.aircraftRegistration || '').trim().toUpperCase();
    return scope === 'AIRCRAFT' && reg && reg !== 'GEN';
  });
}

/**
 * Classify a receiving record by how it was created (PO, loan/rental, from aircraft).
 * @param {object} record
 * @returns {ReceivingContext | 'OTHER'}
 */
export function classifyReceivingRecord(record) {
  if (!record) return 'OTHER';
  const stored = String(record.receivingCategory || '').trim().toUpperCase();
  if (stored === 'AGAINST_PO' || stored === 'LOAN_RENTAL' || stored === 'FROM_AIRCRAFT') {
    return stored;
  }

  const isLoan = String(record.loanDirection || '').trim().toUpperCase() === 'LOAN_IN';
  if (isLoan) return 'LOAN_RENTAL';

  const hasPo = Boolean(
    record.purchaseOrderId
    || String(record.purchaseOrderNumber || '').trim()
  );
  if (hasPo) return 'AGAINST_PO';

  if (hasFromAircraftLines(record)) return 'FROM_AIRCRAFT';

  // Uncategorized drafts (saved before a PO was chosen) belong on Against PO, not hidden.
  if (String(record.workflowStatus || '').toUpperCase() === 'DRAFT') {
    return 'AGAINST_PO';
  }

  return 'OTHER';
}

/**
 * @param {object} record
 * @param {ReceivingContext | null | undefined} context
 */
export function matchesReceivingContext(record, context) {
  if (!context) return true;
  return classifyReceivingRecord(record) === context;
}

/**
 * @param {ReceivingContext | null | undefined} context
 */
export function receivingContextHistoryLabel(context) {
  if (context === 'LOAN_RENTAL') return 'Loan/Rental';
  if (context === 'AGAINST_PO') return 'Against PO';
  if (context === 'FROM_AIRCRAFT') return 'From Aircraft';
  return '';
}

/**
 * @param {string} purpose
 */
export function loanReceivingPurposeLabel(purpose) {
  const p = String(purpose || 'LOAN_IN').trim().toUpperCase();
  if (p === 'LOAN_IN_RETURN') return 'Loan-in return';
  if (p === 'LOAN_OUT_RETURN') return 'Loan-out return';
  if (p === 'LOAN_IN') return 'Loan-in (borrow)';
  return p;
}

/**
 * @param {string} purpose
 * @returns {'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'}
 */
export function loanReceivingPurposeChipColor(purpose) {
  const p = String(purpose || 'LOAN_IN').trim().toUpperCase();
  if (p === 'LOAN_IN_RETURN') return 'warning';
  if (p === 'LOAN_OUT_RETURN') return 'info';
  return 'primary';
}

/**
 * @param {ReceivingContext | null | undefined} context
 * @param {{ isLoanReturn?: boolean, loanPurpose?: string }} [options]
 */
export function receivingContextFormTitle(context, options = {}) {
  const { isLoanReturn = false, loanPurpose = 'LOAN_IN' } = options;
  if (isLoanReturn) {
    return loanReceivingPurposeLabel(loanPurpose);
  }
  if (context === 'AGAINST_PO') return 'Receive against purchase order';
  if (context === 'LOAN_RENTAL') return 'Loan / rental receiving';
  if (context === 'FROM_AIRCRAFT') return 'Receive from aircraft';
  return 'New receiving';
}

/**
 * @param {ReceivingContext | null | undefined} context
 * @param {{ isLoanReturn?: boolean, loanPurpose?: string }} [options]
 */
export function receivingContextFormDescription(context, options = {}) {
  const { isLoanReturn = false, loanPurpose = 'LOAN_IN' } = options;
  if (isLoanReturn && loanPurpose === 'LOAN_IN_RETURN') {
    return 'Record parts returned to the recipient. After inspector approval, quantities are removed from loan inventory and the original loan-in is updated.';
  }
  if (isLoanReturn && loanPurpose === 'LOAN_OUT_RETURN') {
    return 'Record items coming back from a loan-out. After approval, stock is added to store inventory and the loan-out issuance is updated.';
  }
  if (context === 'AGAINST_PO') {
    return 'Receive supplier deliveries against an issued purchase order. PO received quantities update after inspector approval.';
  }
  if (context === 'LOAN_RENTAL') {
    return 'Receive borrowed parts into store inventory using a loan reference (no purchase order). Use Return via Receiving from the Loan page to send stock back.';
  }
  if (context === 'FROM_AIRCRAFT') {
    return 'Receive parts removed from a specific aircraft into store inventory. Select the aircraft tail (not GEN) and enter line details.';
  }
  return 'Submit for Store Inspector / AM approval before inventory or PO totals are updated.';
}

/**
 * @param {ReceivingContext | null | undefined} context
 * @param {{ isLoanReturn?: boolean, loanPurpose?: string }} [options]
 */
export function receivingContextSubmitLabel(context, options = {}) {
  const { isLoanReturn = false, loanPurpose = 'LOAN_IN' } = options;
  if (isLoanReturn) return 'Submit return for approval';
  if (context === 'LOAN_RENTAL') return 'Submit loan-in for approval';
  if (context === 'FROM_AIRCRAFT') return 'Submit aircraft receipt for approval';
  if (context === 'AGAINST_PO') return 'Submit PO receipt for approval';
  return 'Submit for approval';
}

/**
 * @param {ReceivingContext | null | undefined} context
 */
export function receivingHistoryReferenceColumnLabel(context) {
  if (context === 'LOAN_RENTAL') return 'Loan reference';
  if (context === 'AGAINST_PO') return 'PO number';
  if (context === 'FROM_AIRCRAFT') return 'Reference';
  return 'PO / Loan ref';
}

/**
 * @param {ReceivingContext | null | undefined} context
 */
export function receivingContextBannerSeverity(context, isLoanReturn) {
  if (isLoanReturn) return 'warning';
  if (context === 'FROM_AIRCRAFT') return 'info';
  return 'info';
}

/**
 * @param {object} record
 */
export function historyRowLoanPurposeChip(record) {
  if (String(record?.loanDirection || '').toUpperCase() !== 'LOAN_IN') return null;
  const p = String(record.loanReceivingPurpose || 'LOAN_IN').trim().toUpperCase();
  return {
    label: loanReceivingPurposeLabel(p),
    color: loanReceivingPurposeChipColor(p)
  };
}
